// Generic native FirebaseAuth session bridge (Android only), shared by
// every progressively-migrated native page - deliberately NOT owned by
// the Notification Center or any other single feature. See
// android/.../auth/NativeFirebaseSessionPlugin.java and
// NativeFirebaseSessionCoordinator for the native side.
//
// The historical "createNativeCallSession" server function name is
// isolated to this one file - native page code (and the Notification
// Center's own plugin) never sees it, only an opaque customToken string
// handed to the generic bootstrapSession() call.

import { Capacitor } from "@capacitor/core";
import { getFunctions, httpsCallable } from "firebase/functions";

const functions = getFunctions(undefined, "us-central1");

function getNativeFirebaseSessionPlugin() {
  try {
    if (typeof window === "undefined") {
      return null;
    }
    return window.Capacitor?.Plugins?.NativeFirebaseSession ?? null;
  } catch {
    return null;
  }
}

export function isNativeFirebaseSessionAvailable() {
  return Capacitor.getPlatform() === "android" && !!getNativeFirebaseSessionPlugin();
}

/**
 * Establishes (or confirms) a native FirebaseAuth session for the given
 * user via the existing createNativeCallSession Callable - the same
 * server endpoint and client sign-in pattern already proven for native
 * calls, reused here for its already-generic purpose. No-op on
 * non-Android platforms. Best-effort; does not throw.
 *
 * The fetched custom token is never logged and is cleared from this
 * function's own local scope immediately after the native plugin call
 * completes (success or failure).
 */
export async function bootstrapNativeFirebaseSession(user) {
  if (!user?.uid || Capacitor.getPlatform() !== "android") {
    return { ready: false, status: "not_applicable" };
  }

  const plugin = getNativeFirebaseSessionPlugin();
  if (!plugin || typeof plugin.bootstrapSession !== "function") {
    return { ready: false, status: "plugin_not_present" };
  }

  let customToken = "";
  try {
    const createNativeCallSession = httpsCallable(functions, "createNativeCallSession");
    const res = await createNativeCallSession({});
    customToken = String(res?.data?.customToken || "");
    if (!customToken) {
      return { ready: false, status: "no_custom_token" };
    }

    const result = await plugin.bootstrapSession({
      expectedUid: user.uid,
      customToken,
    });
    return { ready: result?.ready === true, status: String(result?.status || "") };
  } catch (err) {
    console.warn("bootstrapNativeFirebaseSession failed:", err);
    return { ready: false, status: "bootstrap_failed" };
  } finally {
    customToken = "";
  }
}

/** Clears the native FirebaseAuth session on logout. No-op on non-Android. Best-effort. */
export async function clearNativeFirebaseSession() {
  if (Capacitor.getPlatform() !== "android") {
    return { cleared: false, status: "not_applicable" };
  }
  const plugin = getNativeFirebaseSessionPlugin();
  if (!plugin || typeof plugin.clearSession !== "function") {
    return { cleared: false, status: "plugin_not_present" };
  }
  try {
    const result = await plugin.clearSession();
    return { cleared: result?.status === "CLEARED", status: String(result?.status || "") };
  } catch (err) {
    console.warn("clearNativeFirebaseSession failed:", err);
    return { cleared: false, status: "clear_failed" };
  }
}
